/**
 * Artifact domain types — mirrors the agentic-backend Artifact data model.
 */

import type { ArtifactShareRole } from '@/types/share-types';

export const ARTIFACT_TYPES = [
  'web_page',
  'document',
  'spreadsheet',
  'presentation',
  'data_viz',
  'image',
  'audio',
  'video',
  'other',
] as const;

export type ArtifactType = (typeof ARTIFACT_TYPES)[number];

export type ArtifactStatus = 'active' | 'archived' | 'deleted';

export type ArtifactThumbnailStatus =
  | 'pending'
  | 'ready'
  | 'failed'
  | 'unsupported';

export type ArtifactFileRole = 'primary' | 'attachment' | 'thumbnail';

export interface ArtifactFile {
  id: string;
  name: string;
  mime_type: string;
  s3_bucket: string;
  s3_key: string;
  s3_region: string;
  generated: boolean;
  role: ArtifactFileRole;
  presigned_url?: string;
  download_url?: string;
  proxy_url?: string;
  created_at: string;
}

export interface ArtifactListItem {
  id: string;
  title: string;
  type: ArtifactType;
  status: ArtifactStatus;
  thumbnail_status: ArtifactThumbnailStatus;
  thumbnail_url?: string;
  client_data: boolean;
  source_workflow_id?: string;
  source_workflow_name?: string;
  source_run_id?: string;
  source_run_started_at?: string;
  conversation_id?: string;
  source_run_created_at?: string;
  /** Chat ``file_id`` this row was promoted from (chat create/edit). */
  source_chat_file_id?: string;
  /** Chat ``file_id`` this row was promoted from (workflow run output). */
  source_node_result_file_id?: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
  /** Embedded primary file — avoids a detail round-trip for open/download. */
  primary_file_name?: string;
  primary_file_mime_type?: string;
  primary_file_s3_bucket?: string;
  primary_file_s3_key?: string;
  primary_file_s3_region?: string;
  primary_file_generated?: boolean;
  primary_presigned_url?: string;
  primary_download_url?: string;
  primary_proxy_url?: string;
  /** Effective role for the requesting user — drives action-menu visibility. */
  user_role?: ArtifactShareRole;
}

export interface ArtifactMetadata extends Record<string, unknown> {
  parent_artifact_id?: string | null;
  version?: number | null;
  edit_summary?: string | null;
}

export interface ArtifactDetail extends ArtifactListItem {
  files: ArtifactFile[];
  artifact_metadata?: ArtifactMetadata;
}

export interface ArtifactListResponse {
  success: boolean;
  artifacts: ArtifactListItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface ArtifactDetailResponse {
  success?: boolean;
  id: string;
  title: string;
  type: ArtifactType;
  status: ArtifactStatus;
  thumbnail_status: ArtifactThumbnailStatus;
  thumbnail_url?: string;
  client_data: boolean;
  source_workflow_id?: string;
  source_workflow_name?: string;
  source_run_id?: string;
  source_run_created_at?: string;
  /** The chat that produced this artifact, for offering to continue it. */
  conversation_id?: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
  files: ArtifactFile[];
  artifact_metadata?: ArtifactMetadata;
  /**
   * Effective role for the requesting user — drives action-menu visibility.
   * The detail handler does not populate this yet, unlike the list and patch
   * responses, so openers still have to carry the role across themselves.
   */
  user_role?: ArtifactShareRole;
}

export interface ArtifactAccessItem {
  user_id: string;
  email?: string | null;
  role_id: string;
  granted_by?: string;
  created_at: string;
}

export interface ArtifactShareResponse {
  success: boolean;
  message: string;
  granted: ArtifactAccessItem[];
  skipped: string[];
}

export interface ArtifactAccessListResponse {
  success: boolean;
  artifact_id: string;
  access: ArtifactAccessItem[];
}

export const ARTIFACT_SORT_KEYS = [
  'updated_at_desc',
  'updated_at_asc',
  'title_asc',
  'title_desc',
] as const;

export type ArtifactSortKey = (typeof ARTIFACT_SORT_KEYS)[number];

/**
 * The sort every reader falls back to when no explicit sort is given.
 * Shared so the gallery and its URL persistence agree on what "absent" means.
 */
export const DEFAULT_ARTIFACT_SORT: ArtifactSortKey = 'updated_at_desc';

// Query params for the list endpoint
export interface ListArtifactsParams {
  scope?: 'mine' | 'shared';
  search?: string; // title, primary file name, or source workflow name
  type?: ArtifactType;
  workflow_id?: string;
  conversation_id?: string;
  source_run_id?: string;
  origin?: 'chat';
  sort?: ArtifactSortKey;
  offset?: number;
  limit?: number;
}

export interface PatchArtifactRequest {
  title?: string;
  status?: ArtifactStatus;
}

export interface ShareArtifactRequest {
  emails: string[];
  role?: 'artifact:owner' | 'artifact:editor' | 'artifact:viewer';
}
